import { useState, useMemo, useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import type { Service, Variante } from '@booking/shared';
import { api } from './api/client';
import { formatHora, formatLongDate } from './lib/dates';
import { useIframeResize, avisarCitaConfirmada } from './hooks/useIframeResize';
import { useLayoutMode } from './hooks/useLayoutMode';
import { useTrafficSource } from './hooks/useTrafficSource';
import { ServicePicker } from './components/ServicePicker';
import { ZonePicker } from './components/ZonePicker';
import { DateTimePicker } from './components/DateTimePicker';
import { ConfirmForm } from './components/ConfirmForm';
import { Spinner } from './components/Spinner';
import { Landing } from './components/Landing';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

type Step =
  | { name: 'service' }
  /* depilación láser: primero se escogen las zonas, que definen precio y minutos */
  | { name: 'zonas'; service: Service; zonas?: string[] }
  | { name: 'datetime'; service: Service; zonas?: string[]; minutos?: number; variante?: Variante }
  | {
      name: 'confirm'; service: Service; date: string; time: string;
      zonas?: string[]; minutos?: number; variante?: Variante;
    }
  | {
      name: 'success'; service: Service; date: string; time: string;
      confirmationCode: string; reservaId: string;
      zonas?: string[]; variante?: Variante;
    };

/** Con zonas, el primer paso es escogerlas; sin ellas, directo al calendario. */
function primerPaso(service: Service, variante?: Variante): Step {
  return service.porZonas
    ? { name: 'zonas', service }
    : { name: 'datetime', service, variante, minutos: variante?.minutos ?? undefined };
}

function getDoctorIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('doctorId');
}

/* Optional: ?serviceId=… opens the calendar on that service, so a link from
   the website can point straight at one procedure. An unknown id is ignored
   and the service picker shows as usual. */
function getServiceIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('serviceId');
}

/** Dispara las conversiones —Google Ads y Meta— una sola vez, cuando aparece
    la pantalla de éxito. */
function Conversion(datos: {
  servicio: string;
  servicioId: string;
  codigo: string;
  reservaId: string;
  precio?: number | null;
  moneda?: string;
}) {
  useEffect(() => {
    avisarCitaConfirmada({
      servicio: datos.servicio,
      servicioId: datos.servicioId,
      codigo: datos.codigo,
      reservaId: datos.reservaId,
      precio: datos.precio ?? undefined,
      moneda: datos.moneda,
    });
  }, [datos.servicio, datos.servicioId, datos.codigo, datos.reservaId, datos.precio, datos.moneda]);
  return null;
}

function Inner() {
  useIframeResize();
  useTrafficSource(); // capture & persist UTM/click IDs on first paint
  const layoutMode = useLayoutMode();
  const doctorId = useMemo(getDoctorIdFromUrl, []);
  const wantedServiceId = useMemo(getServiceIdFromUrl, []);
  const [step, setStep] = useState<Step>({ name: 'service' });
  const [preselected, setPreselected] = useState(false);

  const { data: doctor, isLoading, error } = useQuery({
    queryKey: ['doctor', doctorId],
    queryFn: () => api.doctor(doctorId!),
    enabled: !!doctorId,
    staleTime: 5 * 60_000,
  });

  /* Runs once, as soon as the doctor is known: if the URL named a service the
     doctor actually offers, skip the picker and go to the calendar. */
  useEffect(() => {
    if (preselected || !wantedServiceId || !doctor) return;
    setPreselected(true);
    const service = doctor.services.find((s) => s.id === wantedServiceId);
    if (service) setStep(primerPaso(service));
  }, [doctor, wantedServiceId, preselected]);

  const wrap = (content: React.ReactNode, wide = false) => {
    if (layoutMode === 'landing') {
      return <Landing doctorName={doctor?.name} wide={wide}>{content}</Landing>;
    }
    return <div className={`container${wide ? ' wide' : ''}`}>{content}</div>;
  };

  if (!doctorId) {
    return wrap(
      <div className="error-state">
        <h2>Configuración inválida</h2>
        <p>Falta el parámetro <code>?doctorId</code> en la URL.</p>
      </div>,
    );
  }

  if (error) {
    return wrap(
      <div className="error-state">
        <h2>Error al cargar</h2>
        <p>No pudimos cargar la información del doctor. Por favor recargá la página.</p>
      </div>,
    );
  }

  if (isLoading || !doctor) {
    return wrap(<Spinner />);
  }

  switch (step.name) {
    case 'service':
      return wrap(
        <ServicePicker
          doctor={doctor}
          onPick={(service, variante) => setStep(primerPaso(service, variante))}
        />,
      );
    case 'zonas':
      return wrap(
        <ZonePicker
          service={step.service}
          escogidas={step.zonas}
          onPick={(zonas, minutos) =>
            setStep({ name: 'datetime', service: step.service, zonas, minutos })
          }
          onBack={() => setStep({ name: 'service' })}
        />,
      );
    case 'datetime':
      return wrap(
        <DateTimePicker
          doctorId={doctor.id}
          service={step.service}
          minutos={step.minutos}
          onPick={(date, time) =>
            setStep({
              name: 'confirm',
              service: step.service,
              date,
              time,
              zonas: step.zonas,
              minutos: step.minutos,
              variante: step.variante,
            })
          }
          onBack={() =>
            setStep(
              step.service.porZonas
                ? { name: 'zonas', service: step.service, zonas: step.zonas }
                : { name: 'service' },
            )
          }
        />,
        /* wide */ true,
      );
    case 'confirm':
      return wrap(
        <ConfirmForm
          doctor={doctor}
          service={step.service}
          date={step.date}
          time={step.time}
          zonas={step.zonas}
          minutos={step.minutos}
          variante={step.variante}
          onSuccess={(confirmationCode, reservaId) =>
            setStep({
              name: 'success',
              service: step.service,
              date: step.date,
              time: step.time,
              confirmationCode,
              reservaId,
              zonas: step.zonas,
              variante: step.variante,
            })
          }
          onBack={() =>
            setStep({
              name: 'datetime',
              service: step.service,
              zonas: step.zonas,
              minutos: step.minutos,
              variante: step.variante,
            })
          }
        />,
      );
    case 'success':
      return wrap(
        <div className="success-state">
          <Conversion
            servicio={step.variante?.nombre ?? step.service.name}
            servicioId={step.service.id}
            codigo={step.confirmationCode}
            reservaId={step.reservaId}
            precio={step.variante?.precio ?? step.service.price}
            moneda={step.variante?.moneda ?? step.service.currency}
          />
          <h2>¡Cita confirmada!</h2>
          <p>Fecha: <strong>{formatLongDate(step.date)}</strong></p>
          <p>Hora: <strong>{formatHora(step.time)}</strong></p>
          <p>Servicio: <strong>{step.service.name}</strong></p>
          <p>Código de confirmación: <strong>{step.confirmationCode}</strong></p>
          <p className="hint">Te enviaremos un recordatorio por SMS y email.</p>
        </div>,
      );
  }
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <Inner />
    </QueryClientProvider>
  );
}
